import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Switch,
  Stack,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { format } from 'date-fns';

// --- Interfaces ---
interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumber: boolean;
  requireSpecialChar: boolean;
}

interface EmailSettings {
  enabled: boolean;
  // Add more specific email settings if needed (e.g., server, port, sender address)
}

interface SystemConfig {
  sessionTimeout: number; // In minutes
  passwordPolicy: PasswordPolicy;
  dataRetentionPeriod: number; // In months
  backupSchedule: 'daily' | 'weekly' | 'monthly';
  emailNotifications: EmailSettings;
  // Read-only fields (populated by system/backend)
  creationDate?: Date;
  lastUpdatedDate?: Date;
  createdBy?: string;
  lastUpdatedBy?: string;
}

// --- Initial Data ---
const initialConfig: SystemConfig = {
  sessionTimeout: 30,
  passwordPolicy: {
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumber: true,
    requireSpecialChar: false,
  },
  dataRetentionPeriod: 12,
  backupSchedule: 'daily',
  emailNotifications: {
    enabled: true,
  },
  creationDate: new Date(2023, 0, 1, 10, 0, 0),
  lastUpdatedDate: new Date(2023, 9, 15, 14, 30, 0),
  createdBy: 'System',
  lastUpdatedBy: 'admin.user',
};

// --- Component ---
const SystemConfiguration: React.FC = () => {
  const [config, setConfig] = useState<SystemConfig>(initialConfig);
  const [editMode, setEditMode] = useState(false);
  const [originalConfig, setOriginalConfig] = useState<SystemConfig>(initialConfig); // For cancel

  // Simulate fetching initial config (replace with actual API call)
  useEffect(() => {
    // In a real app, fetch config from backend here
    setConfig(initialConfig);
    setOriginalConfig(initialConfig); // Store original for cancel
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) || 0 : value,
    }));
  };

  const handlePasswordPolicyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setConfig(prev => ({
      ...prev,
      passwordPolicy: {
        ...prev.passwordPolicy,
        [name]: type === 'checkbox' ? checked : parseInt(value) || 0,
      },
    }));
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setConfig(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEmailToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfig(prev => ({
        ...prev,
        emailNotifications: {
            ...prev.emailNotifications,
            enabled: event.target.checked,
        }
    }))
  };

  const handleEdit = () => {
    setOriginalConfig(config); // Store current state before editing
    setEditMode(true);
  };

  const handleSave = () => {
    // In a real app, send the updated 'config' object to the backend here
    console.log("Saving config:", config);
    const updatedConfig = { ...config, lastUpdatedDate: new Date(), lastUpdatedBy: 'current.user' }; // Simulate update
    setConfig(updatedConfig);
    setOriginalConfig(updatedConfig); // Update original state after successful save
    setEditMode(false);
    alert('Configuration saved successfully!'); // Replace with snackbar/toast
  };

  const handleCancel = () => {
    setConfig(originalConfig); // Restore original state
    setEditMode(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/dashboard">
          Home
        </MuiLink>
        <Typography color="text.primary">Administration</Typography>
        <Typography color="text.primary">System Configuration</Typography>
      </Breadcrumbs>

      <Paper sx={{ p: 3, border: '1px solid #E5E7EB', borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            System Configuration
          </Typography>
          {!editMode ? (
            <Button variant="contained" startIcon={<EditIcon />} onClick={handleEdit} sx={{ bgcolor: '#6366F1', '&:hover': { bgcolor: '#4F46E5' } }}>
              Edit
            </Button>
          ) : (
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel} sx={{ color: '#6B7280', borderColor: '#D1D5DB' }}>
                Cancel
              </Button>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} sx={{ bgcolor: '#10B981', '&:hover': { bgcolor: '#059669' } }}>
                Save
              </Button>
            </Stack>
          )}
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {/* --- Session Timeout --- */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Session Timeout (minutes)"
              name="sessionTimeout"
              type="number"
              value={config.sessionTimeout}
              onChange={handleInputChange}
              fullWidth
              disabled={!editMode}
              InputLabelProps={{ shrink: true }}
              InputProps={{ inputProps: { min: 5, max: 120 } }} // Example limits
            />
          </Grid>

          {/* --- Data Retention --- */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Data Retention Period (months)"
              name="dataRetentionPeriod"
              type="number"
              value={config.dataRetentionPeriod}
              onChange={handleInputChange}
              fullWidth
              disabled={!editMode}
              InputLabelProps={{ shrink: true }}
              InputProps={{ inputProps: { min: 1 } }} 
            />
          </Grid>
          
          {/* --- Backup Schedule --- */}
          <Grid item xs={12} md={6}>
            <FormControl component="fieldset" disabled={!editMode}>
              <FormLabel component="legend">Backup Schedule</FormLabel>
              <RadioGroup
                row
                aria-label="Backup Schedule"
                name="backupSchedule"
                value={config.backupSchedule}
                onChange={handleRadioChange}
              >
                <FormControlLabel value="daily" control={<Radio />} label="Daily" />
                <FormControlLabel value="weekly" control={<Radio />} label="Weekly" />
                <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* --- Email Notifications --- */}
          <Grid item xs={12} md={6}>
             <FormControlLabel
                control={
                    <Switch
                        checked={config.emailNotifications.enabled}
                        onChange={handleEmailToggle}
                        name="emailEnabled"
                        disabled={!editMode}
                    />
                }
                label="Enable Email Notifications"
                />
                {/* Add more email fields here if needed */}
          </Grid>

          {/* --- Password Policy --- */}
          <Grid item xs={12}>
             <FormControl component="fieldset" disabled={!editMode} sx={{ width: '100%' }}>
                <FormLabel component="legend">Password Policy</FormLabel>
                <Grid container spacing={2} sx={{ pl: 1, mt: 0.5 }}> {/* Indent policy options */}
                    <Grid item xs={12} sm={4} md={3}>
                         <TextField
                            label="Minimum Length"
                            name="minLength"
                            type="number"
                            size="small"
                            value={config.passwordPolicy.minLength}
                            onChange={handlePasswordPolicyChange}
                            fullWidth
                            disabled={!editMode}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ inputProps: { min: 6, max: 32 } }} 
                        />
                    </Grid>
                    <Grid item xs={12} sm={8} md={9}>
                         <FormGroup row> {/* Place checkboxes horizontally */} 
                            <FormControlLabel control={<Checkbox size="small" name="requireUppercase" checked={config.passwordPolicy.requireUppercase} onChange={handlePasswordPolicyChange} />} label="Uppercase" />
                            <FormControlLabel control={<Checkbox size="small" name="requireLowercase" checked={config.passwordPolicy.requireLowercase} onChange={handlePasswordPolicyChange} />} label="Lowercase" />
                            <FormControlLabel control={<Checkbox size="small" name="requireNumber" checked={config.passwordPolicy.requireNumber} onChange={handlePasswordPolicyChange} />} label="Number" />
                            <FormControlLabel control={<Checkbox size="small" name="requireSpecialChar" checked={config.passwordPolicy.requireSpecialChar} onChange={handlePasswordPolicyChange} />} label="Special Character" />
                         </FormGroup>
                     </Grid>
                </Grid>
            </FormControl>
          </Grid>

          {/* --- Read-only Fields --- */}
          <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
          <Grid item xs={12} sm={6}>
             <TextField label="Creation Date" value={config.creationDate ? format(config.creationDate, 'P p') : 'N/A'} fullWidth disabled InputLabelProps={{ shrink: true }} size="small" />
          </Grid>
           <Grid item xs={12} sm={6}>
             <TextField label="Created By" value={config.createdBy || 'N/A'} fullWidth disabled InputLabelProps={{ shrink: true }} size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
             <TextField label="Last Updated Date" value={config.lastUpdatedDate ? format(config.lastUpdatedDate, 'P p') : 'N/A'} fullWidth disabled InputLabelProps={{ shrink: true }} size="small" />
          </Grid>
           <Grid item xs={12} sm={6}>
             <TextField label="Last Updated By" value={config.lastUpdatedBy || 'N/A'} fullWidth disabled InputLabelProps={{ shrink: true }} size="small" />
          </Grid>

        </Grid>
      </Paper>
    </Box>
  );
};

export default SystemConfiguration; 